import { useGetList, useTranslate } from "ra-core";
import { useId, useMemo, useState } from "react";
import type {
  AutomationEvent,
  AutomationRule,
} from "../../../../supabase/functions/_shared/automation/types.ts";
import { useGavionConfig } from "../config/useGavionConfig";
import { computeDryRun, validateDraft } from "./ruleModel";
import { useIssueText } from "./useIssueText";
import { selectClass } from "./ConditionBuilder";

const json = (v: unknown) => {
  try {
    return JSON.stringify(v) ?? "undefined";
  } catch {
    return String(v);
  }
};

/**
 * "Tester la règle": the last 20 events of the rule's trigger, planned with the tick's own
 * planner. Read-only: this component has no mutation hook, so nothing is written or executed.
 * The rule is validated first; with any error (e.g. unsafe_regex) the planner is never called.
 */
export const DryRunPanel = ({
  rule,
  now,
}: {
  rule: AutomationRule;
  now: () => Date;
}) => {
  const t = useTranslate();
  const selectId = useId();
  const { config } = useGavionConfig();
  const { data: events = [] } = useGetList<AutomationEvent>(
    "automation_events",
    {
      filter: { trigger: rule.trigger },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "created_at", order: "DESC" },
    },
    { retry: false },
  );
  const { errorText } = useIssueText();
  const [eventId, setEventId] = useState("");
  const errors = useMemo(() => validateDraft(rule, null).errors, [rule]);
  const blocked = errors.length > 0;
  const ev = events.find((e) => String(e.id) === eventId);
  const plan = useMemo(
    () => (ev && !blocked ? computeDryRun(rule, ev, config, now()) : null),
    [ev, blocked, rule, config, now],
  );
  return (
    <section
      aria-labelledby={`${selectId}-title`}
      className="flex flex-col gap-3 rounded-xl border p-4"
    >
      <h3 id={`${selectId}-title`} className="font-bold">
        {t("gavion.automation.dry_run.title")}
      </h3>
      <p className="text-xs text-muted-foreground">
        {t("gavion.automation.dry_run.no_write")}
      </p>
      {blocked ? (
        <ul className="flex flex-col gap-1">
          {errors.map((i, k) => (
            <li key={k} role="alert" className="text-sm text-destructive">
              {errorText(i)}
            </li>
          ))}
        </ul>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("gavion.automation.dry_run.none_events")}
        </p>
      ) : (
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={selectId}>
            {t("gavion.automation.dry_run.event")}
          </label>
          <select
            id={selectId}
            className={`${selectClass} w-full sm:w-auto`}
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
          >
            <option value="">{t("gavion.automation.dry_run.pick")}</option>
            {events.map((e) => (
              <option key={e.id} value={String(e.id)}>
                {e.event_id} · {e.created_at}
              </option>
            ))}
          </select>
        </div>
      )}
      {plan && (
        <div aria-live="polite" className="flex flex-col gap-2">
          <p className="font-semibold">
            {t(
              plan.matched
                ? "gavion.automation.dry_run.matched"
                : "gavion.automation.dry_run.not_matched",
            )}
          </p>
          <p className="text-sm font-medium">
            {t("gavion.automation.dry_run.trace")}
          </p>
          <ul className="text-sm">
            {plan.trace.map((tr, i) => (
              <li key={i} className="break-words">
                {tr.result ? "✓" : "✗"} {tr.field}{" "}
                {t(`gavion.automation.ops.${tr.op}`, { _: tr.op })}{" "}
                {json(tr.expected)} (= {json(tr.actual)})
              </li>
            ))}
          </ul>
          {plan.matched &&
            (plan.actions.length === 0 ? (
              <p className="text-sm">
                {t("gavion.automation.dry_run.nothing")}
              </p>
            ) : (
              <ol className="list-decimal pl-5 text-sm">
                {plan.actions.map((a) => (
                  <li key={a.index}>
                    {t(`gavion.automation.actions.${a.type}`, { _: a.type })} —{" "}
                    {a.status === "skipped"
                      ? t("gavion.automation.dry_run.skipped", {
                          reason: a.reason ?? "",
                        })
                      : a.dueAt
                        ? t("gavion.automation.dry_run.due", { date: a.dueAt })
                        : ""}
                    <pre className="overflow-x-auto text-xs whitespace-pre-wrap">
                      {JSON.stringify(a.payload, null, 2)}
                    </pre>
                  </li>
                ))}
              </ol>
            ))}
        </div>
      )}
    </section>
  );
};
